import { EditOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Form, Input } from "antd"
import React, { useEffect, useState } from "react"
import { t } from "~/languages/i18n"
import { JobDetailAttributes, JobDetailFormProps } from "~/types/types"
import { slugPattern } from "~/utils/validationPatterns"
import { checkSlugJobDetail } from "../service/jobDetail"

const removeAccents = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[âÂ]/g, "a")
    .replace(/[êÊ]/g, "e")
    .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
}

const JobDetailForm: React.FC<JobDetailFormProps> = ({ form, onFinishFailed, onFinish }) => {
  const [isEditableSlug, setIsEditableSlug] = useState(true)

  const handleSubmit = () => {
    const formData = form.getFieldsValue()
    const requirementsArray =
      formData.Requirements?.map((item: any) => ({
        name: item.name,
        text: item.text.split("\n").filter((line: string) => line.trim() !== ""), // split by line and remove blank
      })) || []
    const formattedData = {
      ...formData,
      Requirements: requirementsArray,
    }
    onFinish(formattedData)
  }

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = removeAccents(nameValue)

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response = await checkSlugJobDetail(slug)
        return response.data.length === 0
      } catch {
        return false
      }
    }

    const isUnique = await isSlugUnique(slugValue)
    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }
  useEffect(() => {
    try {
      const requirements = form.getFieldValue("Requirements")
      if (requirements) {
        const parsedRequirements = requirements.map((item: any) => ({
          name: item.name,
          text: item.text.join("\n"),
        }))
        form.setFieldsValue({ Requirements: parsedRequirements })
      }
    } catch (e) {
      console.log("Error parsing requirements:", e)
    }
  }, [form.getFieldValue("Requirements")])

  return (
    <Form
      form={form}
      className="flex flex-col w-full"
      name="basic"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
    >
      <Form.Item<JobDetailAttributes>
        name="JobTitle"
        rules={[{ required: true, message: t("Please enter job title!") }]}
        className="mt-4"
      >
        <Input placeholder={t("Add job title")} className="text-xl custom-input" onBlur={handleNameBlur} />
      </Form.Item>

      <div className="flex w-full">
        <Form.Item<JobDetailAttributes>
          className="w-1/2 mr-4"
          name="Slug"
          rules={[
            {
              pattern: slugPattern,
              message: 'Slug must match the following: "A-Z, a-z, 0-9, - ,_ ,. ,~"',
            },
          ]}
          label={<span className="text-2xl font-medium pb-2">{t("Slug")}</span>}
          extra={<span className="text-sm">{t("May not need to be entered (automatically render by name)")}</span>}
        >
          <Input disabled={isEditableSlug} placeholder={t("Slug")} className="custom-input" />
        </Form.Item>
        <Button
          className="self-center mb-2"
          icon={<EditOutlined />}
          color={!isEditableSlug ? "primary" : "default"}
          variant="outlined"
          onClick={() => setIsEditableSlug(!isEditableSlug)}
        >
          {t("Edit")}
        </Button>
      </div>
      <Form.Item<JobDetailAttributes>
        name="Location"
        rules={[{ required: true, message: t("Please enter location!") }]}
        label={<span className="text-2xl font-medium pb-2">{t("Location")}</span>}
        className="mt-4"
      >
        <Input placeholder={t("Add Location")} className="text-xl custom-input" />
      </Form.Item>

      <Form.Item<JobDetailAttributes>
        name="Type"
        label={<span className="text-2xl font-medium pb-2">{t("Type")}</span>}
        rules={[{ required: true, message: t("Please enter type of job!") }]}
        className="mt-4"
      >
        <Input placeholder={t("Add Type")} className="text-xl custom-input" />
      </Form.Item>

      <Form.Item<JobDetailAttributes>
        name="Description"
        rules={[{ required: true, message: t("Please enter description of job!") }]}
        label={<span className="text-2xl font-medium pb-2">{t("Description")}</span>}
        className="mt-4"
      >
        <Input.TextArea rows={10} placeholder={t("Add Description")} className="text-xl custom-input" />
      </Form.Item>
      <Form.List name="Requirements">
        {(fields, { add, remove }) => (
          <>
            <span className="text-2xl font-medium pb-2">{t("Requirements")}</span>
            <div className="border rounded-[10px] p-5 pb-0">
              <div className="grid grid-cols-2 ">
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} className="flex">
                    <div className="flex flex-col mb-[8px] w-[90%]">
                      <Form.Item
                        {...restField}
                        name={[name, "name"]}
                        rules={[{ required: true, message: "Missing name" }]}
                      >
                        <Input placeholder="Name" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "text"]}
                        rules={[{ required: true, message: "Missing text" }]}
                      >
                        <Input.TextArea rows={4} placeholder="Text" />
                      </Form.Item>
                    </div>
                    <div className="flex items-end pb-[32px] pl-2">
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </div>
                  </div>
                ))}
              </div>
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t("Add Requirement")}
                </Button>
              </Form.Item>
            </div>
          </>
        )}
      </Form.List>
    </Form>
  )
}

export default JobDetailForm
